import { call, put, select, takeLatest } from 'redux-saga/effects';
import objectivesApi from 'services/objectivesApi';
import { push } from 'connected-react-router';

import {
  fetchObjectivesRequest,
  saveNewKR,
  updateViewDashboardData
} from 'containers/Objective/store/actions';

import {
  showAppLoading,
  hideAppLoading,
  showAlertMessage,
  handleApiCallError,
  fetchObjectivesForSelectedParams
} from 'containers/App/store/actions';
import { getValueFromQueryParam } from '@xto10x/core/lib/app.utils';

import {
  isHeaderLeftTabs,
  isUnboundedCycle,
  isCustomRangeMode
} from 'utils/app.utils';

import {
  HeaderLeftTabs,
  QueryParam,
  StorageKey,
  ToastActionText,
  ToastActionType,
  ObjectiveTypes
} from 'utils/constants';

import {
  getSelectedTeamIdSelector,
  showCompanyObjectiveSelector,
  showIndependentObjectiveSelector
} from 'containers/Header/store/reducer';
import { getIsAnnualGoalsSpace } from 'containers/App/store/reducer';
import storage from '@xto10x/storage/lib';
import analytics from '@xto10x/core/lib/analytics';
import sentry from '@xto10x/core/lib/sentry';
import { actions } from './actions';
import openAiApis from '../../../services/openAiApis';
import {
  trimAiKeyResults,
  trimAiObjectiveSuggestion
} from '../../../utils/objectives.utils';
import { ChatGptPrompts } from '../../../utils/constants';

const shouldFetchObjectives = createdObjectiveData => {
  const selectedTeamId = getValueFromQueryParam(QueryParam.SELECTED_TEAM);
  const filters = getValueFromQueryParam(QueryParam.FILTERS);

  // get all created objective teams
  const createdObjectiveTeams = [];
  for (let i = 0; i < createdObjectiveData.tags.length; i += 1) {
    createdObjectiveTeams.push(createdObjectiveData.tags[i].tag_id);
  }

  // Return true to fetch objectives only if
  // 1) Current selected tab is All Objectives or one of created objective teams
  // 2) Current selected tab is My Objectives and created objective owner is same as current owner
  return (
    selectedTeamId === HeaderLeftTabs[2].tag_id ||
    (selectedTeamId === HeaderLeftTabs[0].tag_id &&
      createdObjectiveData.owner_detail.user_reference_id ===
        storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID)) ||
    createdObjectiveTeams.includes(selectedTeamId) ||
    filters
  );
};

function* createObjectiveWorker({ requestBody, rootObjectiveIndex, callback }) {
  try {
    yield put(showAppLoading()); // we should remove this and make a global call
    const message =
      requestBody && requestBody.parent_objective_id
        ? 'Key result added'
        : 'Objective created';
    const { data } = yield objectivesApi.createObjective(requestBody);
    if (requestBody.parent_objective_id) {
      const obj = { ...data, rootObjectiveIndex };
      yield put(saveNewKR(obj, requestBody.parent_objective_id));
      yield put(updateViewDashboardData(true));
    } else if (shouldFetchObjectives(data)) {
      const selectedTeamId = yield select(getSelectedTeamIdSelector);
      const showCompanyObjectivesOnly = yield select(
        showCompanyObjectiveSelector
      );
      const showIndependentObjectivesOnly = yield select(
        showIndependentObjectiveSelector
      );

      const filters = getValueFromQueryParam(QueryParam.FILTERS);

      if (filters) {
        const ORG_SUB_DOMAIN = storage.getDataFromLocal(StorageKey.ORG_INFO)
          ?.sub_domain;
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(QueryParam.FILTERS);
        yield put(
          push(`/${ORG_SUB_DOMAIN}/objectives?${searchParams.toString()}`)
        );
        yield put(hideAppLoading());
        const alertAction =
          requestBody && requestBody.parent_objective_id
            ? null
            : {
                actionText: ToastActionText.VIEW,
                actionType: ToastActionType.HIGHLIGHT_OBJECTIVE,
                actionData: {
                  ...data,
                  isAnnualSpace: yield select(getIsAnnualGoalsSpace)
                }
              };
        if (callback) callback({ success: true, data });
        yield put(showAlertMessage('success', message, alertAction)); // params: type, message, actionable, actionableText, actionableData
        return;
      }
      const requestParams = {
        okr_cycle_id: getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE),
        tag_id: !isHeaderLeftTabs(
          getValueFromQueryParam(QueryParam.SELECTED_TEAM)
        )
          ? getValueFromQueryParam(QueryParam.SELECTED_TEAM)
          : null,
        user_hierarchy_type:
          selectedTeamId === HeaderLeftTabs[1].tag_id ? 'DIRECT_REPORTS' : null,
        user_id:
          HeaderLeftTabs[0].tag_id ===
          getValueFromQueryParam(QueryParam.SELECTED_TEAM)
            ? storage.getDataFromLocal(StorageKey.REQUESTER_USER_ID)
            : null,
        objective_type:
          selectedTeamId === HeaderLeftTabs[2].tag_id
            ? `${showCompanyObjectivesOnly ? ObjectiveTypes.company : ''}${
                showIndependentObjectivesOnly
                  ? `${showCompanyObjectivesOnly ? ',' : ''}${
                      ObjectiveTypes.nonAligned
                    }`
                  : ''
              }`
            : ''
      };
      if (!requestParams.objective_type) delete requestParams.objective_type;
      if (
        !isCustomRangeMode() &&
        !isUnboundedCycle(data.objective_cycle.cycle_type)
      ) {
        yield put(fetchObjectivesRequest(requestParams));
        yield put(updateViewDashboardData(true));
      } else if (isCustomRangeMode())
        yield put(fetchObjectivesForSelectedParams());
    }
    yield put(hideAppLoading());
    const alertAction =
      requestBody && requestBody.parent_objective_id
        ? null
        : {
            actionText: ToastActionText.VIEW,
            actionType: ToastActionType.HIGHLIGHT_OBJECTIVE,
            actionData: data
          };
    if (callback) callback({ success: true, data });
    yield put(showAlertMessage('success', message, alertAction)); // params: type, message, actionable, actionableText, actionableData
  } catch (e) {
    yield put(hideAppLoading());
    if (e?.response?.data?.code === 279) {
      const error = new Error('Start date not present in create objective');
      sentry.captureException(error);
    }
    yield put(
      handleApiCallError(e, {
        type: 'CREATE_OBJECTIVE',
        data: { requestBody, rootObjectiveIndex, callback }
      })
    );
    analytics.trackError('create_objective_worker', e);
  }
}

function* createObjectiveKrWorker({ requestBody, callback }) {
  try {
    yield put(showAppLoading());
    const message = 'Objective created';
    const {
      data
    } = yield objectivesApi.createObjectiveAndKeyResultsWithCheckIns(
      requestBody
    );
    yield put(hideAppLoading());
    if (callback) callback({ success: true, data });
    yield put(showAlertMessage('success', 'Objective created'));
  } catch (e) {
    yield put(hideAppLoading());
    yield put(
      handleApiCallError(e, {
        type: 'CREATE_OBJECTIVE_KRS_CHECKINS',
        data: { requestBody, callback }
      })
    );
    analytics.trackError('create_objective_worker', e);
  }
}

function* getAiSuggestedOkrTitlweorker({ prompt, callback }) {
  try {
    yield put(showAppLoading());
    const { data } = yield openAiApis.getAiSuggestedOkr({
      prompt
    });
    if (data) {
      callback(trimAiObjectiveSuggestion(data));
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    callback(null, true);
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('get_ai_suggested_okr_title_worker', e);
  }
}

function* getAiSuggestedKrsFromOkrTitlworker({ prompt, callback }) {
  try {
    yield put(showAppLoading());
    const { data } = yield openAiApis.getKrSuggestionBasedOnApi({
      prompt
    });
    if (data && callback) {
      callback(trimAiKeyResults(data));
    }
    yield put(hideAppLoading());
  } catch (e) {
    yield put(hideAppLoading());
    callback(null, true);
    yield put(showAlertMessage('error', e.message));
    analytics.trackError('get_ai_suggested_okr_title_worker', e);
  }
}

export default function* defaultSaga() {
  yield takeLatest(actions.CREATE_OBJECTIVE, createObjectiveWorker);
  yield takeLatest(
    actions.CREATE_OBJECTIVE_KR_CHECKINS,
    createObjectiveKrWorker
  );
  yield takeLatest(
    actions.GET_AI_SUGGESTED_OKR_TITLE,
    getAiSuggestedOkrTitlweorker
  );
  yield takeLatest(
    actions.GET_KRS_SUGGESTED_BY_AI_ON_OBJECTIVE,
    getAiSuggestedKrsFromOkrTitlworker
  );
}
